import { Box } from '@chakra-ui/layout';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from 'urql';

import { useGetLoggedInUser } from '@revelio/auth';
import { PagePaths } from '@revelio/core';

import { DeliverableStepContainer } from '../../../../deliverables/deliverable-step-container';
import { useReportBuilderConfig } from '../../../hooks/useReportBuilderConfig';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../../../utils/report-builder-paths';
import {
  reportBuilderStore,
  useAllReports,
} from '../../report-builder.repository';
import { CreateReportsMutation } from '../../report-builder-operations';
import { generatePayload } from '../utils';
import { ReportSummary } from './report-summary';

export const MwReportConfirmation = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [, createReport] = useMutation(CreateReportsMutation);
  const { loggedInUser } = useGetLoggedInUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);

  const reportEntityMap = useReportBuilderConfig().config;
  const reportBuilderDeliverables = useAllReports();
  const report = Object.values(reportBuilderDeliverables)[0];

  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '');
    setName(cleaned);
    const isValid = cleaned.trim().length > 0;
    setNameError(isValid ? null : 'Report name is required.');
    setIsSubmitDisabled(!isValid);
  };

  const onSubmit = async () => {
    if (!report) {
      return;
    }

    setIsLoading(true);

    const reportName = name.trim();
    if (!reportName) {
      toast({
        title: 'Please enter a report name',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      setIsLoading(false);
      return;
    }

    const payload = generatePayload(report, reportEntityMap);
    if (!payload) {
      toast({
        title: 'Unable to prepare report payload',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await createReport({
        input: {
          client: loggedInUser?.client_name || '',
          name: reportName,
          reports: [payload],
        },
      });

      if (error) {
        toast({
          title: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      toast({
        title: 'Report request sent successfully',
        description: 'Check back in a few minutes.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });

      navigate(`/${PagePaths.REPORTS}`);
      reportBuilderStore.actions.reset();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Unexpected error occurred';
      toast({
        title: 'Failed to submit report request',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!report) {
      navigate(getReportBuilderPath(ReportBuilderStep.REPORT_TYPE));
    }
  }, [report, navigate]);

  if (!report) {
    return null;
  }
  return (
    <DeliverableStepContainer
      id="report_builder_confirmation"
      title="Review Your Report and Confirm"
      previousStepPath={getReportBuilderPath(ReportBuilderStep.ENTITY)}
      submitButtonContent="Submit"
      onSubmit={onSubmit}
      startOverPath={getReportBuilderPath(ReportBuilderStep.REPORT_TYPE)}
      isSubmitDisabled={isSubmitDisabled}
      isSubmitLoading={isLoading}
      isReportStep={true}
    >
      <Box p="8">
        <ReportSummary report={report} />
        <FormControl id="report_name" isInvalid={!!nameError} mt="10">
          <FormLabel fontSize="sm" color="text.primary">
            Report Name
          </FormLabel>
          <Input
            value={name}
            onChange={onChangeName}
            placeholder="Enter unique report name"
            width={350}
          />
          <FormErrorMessage>{nameError}</FormErrorMessage>
        </FormControl>
      </Box>
    </DeliverableStepContainer>
  );
};
